/**
 * Authenticated app shell.
 *
 * Mounts the header once and renders nested routes via `<Outlet />`.
 * Also calls `useAuthBootstrap` so the proactive-refresh timer and
 * window-focus listener spin up exactly once per session — mounting
 * them inside individual pages would multiply the timers.
 *
 * v1.1 A5: the outer container is locked to exactly viewport height
 * (`h-screen overflow-hidden`) so the body never scrolls on the
 * dashboard. The `Grid` inside owns the only vertical scrollbar —
 * before, `min-h-screen` plus `Grid`'s own `overflow-y-auto` produced
 * two stacked scrollbars whenever the viewport math rounded slightly
 * past 100 vh. AdminLayout intentionally keeps `min-h-screen` because
 * long admin tables need body-scroll to behave normally.
 *
 * DISPLAY (TV kiosk) sessions are redirected to the full-screen Permit
 * Wall: their only screen is /wall, with no path into the card-grid
 * shell. This guard is the robust backstop for any entry point (deep
 * link, stale tab) that the role-aware login/index redirects miss.
 */

import { Navigate, Outlet } from "react-router-dom";
import { Header } from "./Header";
import { TopProgressBar } from "./TopProgressBar";
import { useAuthBootstrap } from "@/hooks/useAuth";
import { useAuthStore } from "@/stores/authStore";

export function AppLayout() {
  useAuthBootstrap();
  const role = useAuthStore((s) => s.user?.role);
  if (role === "DISPLAY") return <Navigate to="/wall" replace />;
  return (
    <div className="h-screen flex flex-col overflow-hidden bg-canvas dark:bg-canvas-dark">
      {/* v1.1 C4 — global top-of-page progress affordance. Mounts
          once per layout so a single bar coordinates across pages. */}
      <TopProgressBar />
      <Header />
      <main className="flex-1 min-h-0">
        <Outlet />
      </main>
    </div>
  );
}
